/** 从全局默认克隆产品 / 提示词 / LLM 配置到本公司（POST /api/admin/clone-global） */
import { useEffect, useState } from "react";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { Alert, Button, Dialog } from "@/components/ui";
import { useCloneGlobal } from "./hooks";
import type { CloneGlobalResult } from "./api";

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (r: CloneGlobalResult) => void;
}

export function CloneGlobalDialog({ open, onClose, onDone }: Props) {
  const [force, setForce] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mut = useCloneGlobal();

  useEffect(() => {
    if (open) {
      setForce(false);
      setErr(null);
    }
  }, [open]);

  const submit = async () => {
    setErr(null);
    try {
      const res = await mut.mutateAsync(force);
      onDone(res.cloned);
    } catch (e) {
      setErr(e instanceof ApiError
        ? ((e.body as { detail?: string } | null)?.detail || `克隆失败 (${e.status})`)
        : "克隆失败");
    }
  };

  const option = (value: boolean, title: string, desc: string) => (
    <label
      className={cn(
        "flex cursor-pointer gap-2.5 rounded-lg border p-3 transition-colors",
        force === value
          ? value ? "border-danger/50 bg-danger-soft ring-1 ring-danger/40" : "border-brand bg-primary-soft ring-1 ring-brand"
          : "hover:bg-muted",
      )}
    >
      <input
        type="radio"
        name="clone-mode"
        checked={force === value}
        onChange={() => setForce(value)}
        className={cn("mt-0.5", value ? "accent-danger" : "accent-primary")}
      />
      <span>
        <span className="block font-medium text-foreground">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{desc}</span>
      </span>
    </label>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="从全局默认克隆"
      description="把系统默认的产品、价格规则、提示词和 LLM 配置复制到本公司。"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button variant={force ? "danger" : "primary"} onClick={submit} loading={mut.isPending}>
            {mut.isPending ? "克隆中…" : force ? "覆盖并克隆" : "开始克隆"}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {option(false, "跳过已有", "本公司已存在的同标识产品 / 提示词保持不变，只补充缺少的。")}
        {option(true, "强制覆盖", "先删除本公司全部产品、提示词和 LLM 配置，再整体克隆。自定义内容会丢失，不可恢复。")}
        {err && <Alert>{err}</Alert>}
      </div>
    </Dialog>
  );
}
